import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getAdminOrdersApi, getAdminOrderDetailApi, updateOrderStatusApi } from '../../api/adminApi';
import imgUrl from '../../utils/imgUrl';

const STATUS_TABS = ['all','pending','confirmed','processing','shipped','delivered','cancelled'];
const STATUS_COLOR = {
  pending: { bg:'#FEF3C7', fg:'#D97706' }, confirmed: { bg:'#DBEAFE', fg:'#2563EB' },
  processing: { bg:'#EDE9FE', fg:'#7C3AED' }, shipped: { bg:'#CFFAFE', fg:'#0891B2' },
  delivered: { bg:'#DCFCE7', fg:'#16A34A' }, cancelled: { bg:'#FEE2E2', fg:'#DC2626' },
};

function StatusBadge({ s }) {
  const c = STATUS_COLOR[s] || { bg: '#F3F4F6', fg: '#6B7280' };
  return <span style={{ display:'inline-block', fontSize:'10px', fontWeight:700, letterSpacing:'.5px', textTransform:'capitalize', padding:'3px 10px', borderRadius:'99px', background:c.bg, color:c.fg }}>{s}</span>;
}

export default function OrdersTab() {
  const [orders, setOrders]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [status, setStatus]   = useState('all');
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail]   = useState(null); // selected order details
  const [detailLoading, setDetailLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [trackNum, setTrackNum] = useState('');

  const LIMIT = 15;

  const load = () => {
    setLoading(true);
    getAdminOrdersApi({ status: status === 'all' ? '' : status, search, page, limit: LIMIT })
      .then((r) => { setOrders(r.data.orders || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error('Failed to load orders'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [status, search, page]); // eslint-disable-line

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail('loading');
    try {
      const r = await getAdminOrderDetailApi(id);
      setDetail(r.data);
      setTrackNum(r.data.tracking_number || '');
    } catch { toast.error('Failed to load order'); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await updateOrderStatusApi(id, { status: newStatus, tracking_number: trackNum || undefined });
      toast.success('Order status updated');
      if (detail && detail.id === id) setDetail({ ...detail, status: newStatus });
      load();
    } catch { toast.error('Update failed'); }
    finally { setUpdatingId(null); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
      {/* Left: Orders List */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.hdr}>
          <div>
            <h1 style={S.title}>Orders</h1>
            <p style={S.sub}>{total} orders found</p>
          </div>
        </div>

        {/* Status Tabs */}
        <div style={S.tabs}>
          {STATUS_TABS.map((t) => (
            <button key={t} style={{ ...S.tab, borderBottom: status === t ? '2px solid var(--red)' : '2px solid transparent', color: status === t ? 'var(--red)' : '#6B7280', fontWeight: status === t ? 700 : 500 }}
              onClick={() => { setStatus(t); setPage(1); }}>
              {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Search */}
        <div style={S.filterBar}>
          <div style={S.searchWrap}>
            <span style={S.searchIco}>🔍</span>
            <input style={S.searchInp} placeholder="Search by customer name or order ID..." value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          </div>
        </div>

        {/* Table */}
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>{['Order ID','Customer','Amount','Payment','Status','Date','Action'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading
                ? <tr><td colSpan={7} style={{ textAlign:'center', padding:'48px', color:'#aaa' }}>Loading...</td></tr>
                : orders.length === 0
                  ? <tr><td colSpan={7} style={{ textAlign:'center', padding:'48px', color:'#aaa' }}>No orders found</td></tr>
                  : orders.map((o) => (
                    <tr key={o.id} style={{ ...S.tr, background: detail?.id === o.id ? '#FFF5F5' : undefined }} onClick={() => openDetail(o.id)}>
                      <td style={S.td}><span style={S.orderId}>#{o.id.slice(0,8).toUpperCase()}</span></td>
                      <td style={S.td}>
                        <p style={{ fontWeight:600, margin:0, color:'#111' }}>{o.customer_name}</p>
                        <p style={{ fontSize:'11px', color:'#9CA3AF', margin:0 }}>{o.customer_email}</p>
                      </td>
                      <td style={S.td}><strong>₹{parseFloat(o.total_amount).toLocaleString('en-IN')}</strong></td>
                      <td style={S.td}><span style={S.methodBadge}>{(o.payment_method || 'COD').toUpperCase()}</span></td>
                      <td style={S.td}><StatusBadge s={o.status} /></td>
                      <td style={S.td}>{new Date(o.placed_at).toLocaleDateString('en-IN')}</td>
                      <td style={S.td} onClick={(e) => e.stopPropagation()}>
                        <select style={S.statusSelect} value={o.status}
                          onChange={(e) => handleStatusUpdate(o.id, e.target.value)}
                          disabled={updatingId === o.id}>
                          {['pending','confirmed','processing','shipped','delivered','cancelled'].map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={S.pagination}>
            <button style={S.pgBtn} disabled={page === 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
            <span style={{ fontSize:'13px', color:'#666' }}>Page {page} of {totalPages}</span>
            <button style={S.pgBtn} disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
          </div>
        )}
      </div>

      {/* Right: Order Detail Panel */}
      {detail && (
        <div style={S.detailPanel}>
          <div style={S.detailHead}>
            <span style={S.detailTitle}>ORDER DETAIL</span>
            <button style={S.closeBtn} onClick={() => setDetail(null)}>✕</button>
          </div>

          {detail === 'loading' || detailLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>Loading...</div>
          ) : (
            <div style={{ padding: '16px', overflowY: 'auto', flex: 1 }}>
              {/* Order meta */}
              <div style={S.detailMeta}>
                <p style={S.detailOrderId}>#{detail.id.slice(0,8).toUpperCase()}</p>
                <StatusBadge s={detail.status} />
              </div>
              <p style={{ fontSize:'12px', color:'#9CA3AF', marginBottom:'16px' }}>
                {new Date(detail.placed_at).toLocaleString('en-IN')}
              </p>

              {/* Customer */}
              <div style={S.detailSection}>
                <p style={S.detailSectionTitle}>CUSTOMER</p>
                <p style={{ fontWeight:700, marginBottom:'2px' }}>{detail.customer_name}</p>
                <p style={{ fontSize:'12px', color:'#6B7280', margin:'1px 0' }}>{detail.customer_email}</p>
                <p style={{ fontSize:'12px', color:'#6B7280', margin:'1px 0' }}>{detail.customer_phone}</p>
              </div>

              {/* Shipping Address */}
              {detail.shipping_address && (
                <div style={S.detailSection}>
                  <p style={S.detailSectionTitle}>SHIP TO</p>
                  <p style={{ fontSize:'13px', color:'#374151', lineHeight:1.7, margin:0 }}>
                    {detail.shipping_address.full_name}<br />
                    {detail.shipping_address.address_line1}{detail.shipping_address.address_line2 ? ', ' + detail.shipping_address.address_line2 : ''}<br />
                    {detail.shipping_address.city}, {detail.shipping_address.state} — {detail.shipping_address.postal_code}
                  </p>
                </div>
              )}

              {/* Items */}
              <div style={S.detailSection}>
                <p style={S.detailSectionTitle}>ITEMS ({(detail.items || []).length})</p>
                {(detail.items || []).map((item, i) => (
                  <div key={i} style={S.orderItem}>
                    <div style={S.itemThumb}>
                      {item.image ? <img src={imgUrl(item.image)} alt={item.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : '👕'}
                    </div>
                    <div style={{ flex:1 }}>
                      <p style={{ fontWeight:600, fontSize:'12px', margin:'0 0 2px' }}>{item.name}</p>
                      <p style={{ fontSize:'11px', color:'#9CA3AF', margin:0 }}>Qty: {item.quantity} × ₹{parseFloat(item.unit_price).toLocaleString('en-IN')}</p>
                    </div>
                    <p style={{ fontWeight:700, fontSize:'13px', margin:0, flexShrink:0 }}>₹{(item.quantity * parseFloat(item.unit_price)).toLocaleString('en-IN')}</p>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div style={S.detailSection}>
                <p style={S.detailSectionTitle}>PAYMENT</p>
                <div style={S.totalRow}><span>Subtotal</span><span>₹{parseFloat(detail.total_amount).toLocaleString('en-IN')}</span></div>
                <div style={{ ...S.totalRow, fontWeight:700, fontSize:'15px', marginTop:'8px', paddingTop:'8px', borderTop:'1px solid #F3F4F6' }}>
                  <span>Total</span><span>₹{parseFloat(detail.total_amount).toLocaleString('en-IN')}</span>
                </div>
                <p style={{ fontSize:'11px', color:'#9CA3AF', marginTop:'6px' }}>Payment: {(detail.payment_method || 'COD').toUpperCase()}</p>
              </div>

              {/* Update status */}
              <div style={S.detailSection}>
                <p style={S.detailSectionTitle}>UPDATE STATUS</p>
                <div style={{ display:'flex', gap:'8px', flexDirection:'column' }}>
                  <input style={S.trackInp} placeholder="Tracking number (optional)" value={trackNum} onChange={(e) => setTrackNum(e.target.value)} />
                  <select style={S.statusSelect} value={detail.status}
                    onChange={(e) => handleStatusUpdate(detail.id, e.target.value)}>
                    {['pending','confirmed','processing','shipped','delivered','cancelled'].map((s) => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const S = {
  hdr:          { display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'16px' },
  title:        { fontFamily:'var(--font-display)', fontSize:'24px', letterSpacing:'1.5px', color:'#111', margin:0 },
  sub:          { fontSize:'13px', color:'#9CA3AF', marginTop:'2px' },
  tabs:         { display:'flex', gap:'0', borderBottom:'1px solid #E5E5E5', marginBottom:'14px', overflowX:'auto' },
  tab:          { padding:'10px 16px', background:'none', border:'none', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:'12px', letterSpacing:'.5px', textTransform:'capitalize', transition:'all .15s', whiteSpace:'nowrap' },
  filterBar:    { marginBottom:'14px' },
  searchWrap:   { position:'relative' },
  searchIco:    { position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', fontSize:'13px' },
  searchInp:    { width:'100%', border:'1.5px solid #E5E5E5', background:'#fff', padding:'9px 12px 9px 36px', fontFamily:'var(--font-body)', fontSize:'13px', outline:'none', boxSizing:'border-box' },
  tableWrap:    { background:'#fff', border:'1px solid #E5E5E5', overflow:'auto' },
  table:        { width:'100%', borderCollapse:'collapse', minWidth:'700px' },
  th:           { padding:'10px 14px', fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:'#9CA3AF', borderBottom:'1px solid #E5E5E5', textAlign:'left', background:'#FAFAFA', whiteSpace:'nowrap' },
  tr:           { borderBottom:'1px solid #F9FAFB', cursor:'pointer', transition:'background .1s' },
  td:           { padding:'11px 14px', fontSize:'13px', verticalAlign:'middle' },
  orderId:      { fontFamily:'var(--font-display)', fontSize:'12px', letterSpacing:'1px', color:'var(--red)' },
  methodBadge:  { background:'#F3F4F6', color:'#374151', fontSize:'10px', fontWeight:700, padding:'2px 7px' },
  statusSelect: { border:'1.5px solid #E5E5E5', padding:'5px 8px', fontFamily:'var(--font-body)', fontSize:'11px', outline:'none', background:'#fff', cursor:'pointer', width:'100%' },
  pagination:   { display:'flex', justifyContent:'center', alignItems:'center', gap:'16px', marginTop:'16px' },
  pgBtn:        { background:'#fff', border:'1.5px solid #E5E5E5', padding:'7px 16px', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:600, cursor:'pointer' },
  detailPanel:  { width:'320px', flexShrink:0, background:'#fff', border:'1px solid #E5E5E5', display:'flex', flexDirection:'column', maxHeight:'calc(100vh - 120px)', position:'sticky', top:'80px' },
  detailHead:   { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px', borderBottom:'1px solid #E5E5E5', background:'#FAFAFA', flexShrink:0 },
  detailTitle:  { fontFamily:'var(--font-display)', fontSize:'11px', letterSpacing:'2.5px', color:'#9CA3AF' },
  closeBtn:     { background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', fontSize:'14px', padding:'2px 6px' },
  detailMeta:   { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' },
  detailOrderId:{ fontFamily:'var(--font-display)', fontSize:'16px', letterSpacing:'1.5px', margin:0, color:'#111' },
  detailSection:{ borderTop:'1px solid #F3F4F6', paddingTop:'12px', marginTop:'12px' },
  detailSectionTitle:{ fontSize:'9px', fontWeight:700, letterSpacing:'2px', color:'#9CA3AF', textTransform:'uppercase', marginBottom:'8px' },
  orderItem:    { display:'flex', alignItems:'center', gap:'10px', marginBottom:'10px' },
  itemThumb:    { width:'40px', height:'48px', background:'#F3F4F6', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', fontSize:'16px' },
  totalRow:     { display:'flex', justifyContent:'space-between', fontSize:'13px', color:'#374151', marginBottom:'4px' },
  trackInp:     { width:'100%', border:'1.5px solid #E5E5E5', padding:'8px 10px', fontFamily:'var(--font-body)', fontSize:'12px', outline:'none', boxSizing:'border-box' },
};
